import { zodResolver } from '@hookform/resolvers/zod';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { iProfessional, iStore } from '~/domain/interfaces/models';
import { translator } from '~/presentation/components/i18n';
import InputKeyboardDate from '~/presentation/components/inputKeyboardDate';
import { Button, Input } from '~/presentation/components/UI';
import { schemaGeneralData } from '~/validation/validators/document/MedicalCertificateSideBarValidator';
import { iCreateMedicalCertificate, iListConsultant } from '../interface';
import {
  Container,
  Content,
  Form,
  Navigation,
} from '../styles/StyledGeneralData';
import _ from 'lodash';
import SearchSelect from '~/presentation/components/UI/searchSelect';

export interface ownProps {
  consultant: iListConsultant[];
  next: (data: iCreateMedicalCertificate) => any;
  professional: iProfessional;
  state?: iCreateMedicalCertificate;
}

interface iStateParams {
  appointmentId: string;
  professionalId: number;
  consultantId: number;
}

const GeneralData: React.FC<ownProps> = ({
  next,
  professional,
  state,
}): JSX.Element => {
  const { info } = useSelector((store: iStore) => store.auth);
  const { orgId } = useSelector((store: iStore) => store.auth.selectUser);

  let stateParams = useLocation<iStateParams>().state;
  stateParams = {
    ...stateParams,
    professionalId:
      info.professionals?.find(prof => prof.org.id === orgId)?.id ??
      stateParams.professionalId,
  };

  const [professionalId, setProfId] = useState<number | undefined>(
    stateParams.professionalId,
  );

  const { errors, handleSubmit, register, setValue, watch } = useForm({
    mode: 'onChange',
    shouldFocusError: true,
    resolver: zodResolver(schemaGeneralData),
    defaultValues: {
      ...state,
    },
  });

  const onSubmit = handleSubmit(data => {
    next({ ...data });
  });

  useEffect(() => {
    register('professional');
    if (stateParams.professionalId) {
      setProfId(stateParams.professionalId);
      setValue('professional', stateParams.professionalId);
    }
  }, []);

  const generateProfessionalSelectOptions = useCallback(() => {
    return _.sortBy(
      professional?.results.flatMap(value => {
        return [
          {
            value: value.professional.id,
            label: `${value.user.firstName} ${value.user.lastName}`,
          },
        ];
      }),
      item => item?.label.replace(/[^a-zA-Z0-9]/g, '').toLocaleLowerCase(),
    );
  }, [professional]);

  const professionalMultiselectSelected = useMemo(() => {
    return generateProfessionalSelectOptions().find(
      option => option.value === watch('professional'),
    );
  }, [generateProfessionalSelectOptions, watch]);

  return (
    <Container>
      <Content>
        <Form onSubmit={onSubmit}>
          <Input
            id="input_instituition"
            label={`${translator('Instituição')}`}
            onChange={e => setValue('org', Number(e.target.value))}
            name="org"
            register={() => register('org')}
            error={Boolean(errors.org)}
            message={errors?.org?.message}
            placeholder={state?.orgUnitName}
            disabled={state && state.orgUnitName !== ''}
            autoFocus
          />
          <SearchSelect
            key={professionalId}
            id="select_professional"
            name="professional"
            register={() => register('professional')}
            label={`${translator('Profissional')}`}
            placeholder={translator('Selecione um profissional')}
            onChange={e => {
              setProfId(Number(e?.value));
              setValue('professional', Number(e?.value));
            }}
            error={Boolean(errors.professional)}
            message={
              errors?.professional?.message
                ? translator(errors?.professional?.message)
                : ''
            }
            isDisabled={Boolean(professionalId && professionalId !== -1)}
            options={generateProfessionalSelectOptions()}
            value={professionalMultiselectSelected}
          />
          <InputKeyboardDate
            state={new Date()}
            setState={() => ''}
            label={`${translator('Data de emissão')}`}
            placeholder="DD/MM/AAAA"
            disabled
          />
        </Form>
        <Navigation justifyEnd>
          <Button variant="primary" onClick={onSubmit} size="medium" rounded>
            Próximo
          </Button>
        </Navigation>
      </Content>
    </Container>
  );
};

export default GeneralData;
